import { pb } from "@/lib/pocketbase";
import { SanityCredentials, SanityPublishOptions } from "@/types/integration";

export interface SanityPublishRequest {
  brandId: string;
  postId?: string;
  title: string;
  content: string;
  slug: string;
  metaDescription?: string;
  featuredImage?: string;
  status: string;
  documentType?: string;
  imageFieldName?: string;
  returnIds?: boolean;
  returnDocuments?: boolean;
  visibility?: "sync" | "async" | "deferred";
  dryRun?: boolean;
}

export interface SanityBatchPublishRequest {
  brandId: string;
  posts: SanityPublishRequest[];
}

export interface SanityQueryRequest {
  brandId: string;
  query: string;
  params?: Record<string, unknown>;
}

export interface SanityConnectionTest {
  brandId: string;
  projectId: string;
  dataset: string;
  token: string;
  studioUrl?: string;
}

export interface SanityPublishResponse {
  document_id: string;
  studio_url: string;
  message: string;
}

export interface SanityBatchPublishResponse {
  document_ids: string[];
  count: number;
  message: string;
}

export interface SanityQueryResponse {
  ms: number;
  query: string;
  result: unknown;
  syncTags?: string[];
}

export interface SanityValidationResult {
  valid: boolean;
  errors?: string[];
  warnings?: string[];
  skipped?: string[];
}

export interface SanitySchemaField {
  name: string;
  type: string;
  title?: string;
  description?: string;
  required?: boolean;
  readOnly?: boolean;
  hidden?: boolean;
}

export interface SanitySchemaType {
  name: string;
  type: string;
  title?: string;
  description?: string;
  fields: SanitySchemaField[];
}

export interface SanityDocumentTypesResponse {
  document_types: string[];
  validation: Record<string, SanityValidationResult>;
}

export interface SanitySchemaValidationResponse {
  document_type: string;
  schema: SanitySchemaType;
  validation: SanityValidationResult;
}

export interface SanityUpdateSettingsRequest {
  brandId: string;
  autoPublish?: boolean;
  selectedDocumentType?: string;
  imageFieldName?: string;
  defaultAuthor?: string;
  defaultPublishOptions?: SanityPublishOptions;
}

export interface SanitySettingsResponse {
  message: string;
  settings: {
    autoPublish?: boolean;
    selectedDocumentType?: string;
    imageFieldName?: string;
    defaultAuthor?: string;
    defaultPublishOptions?: SanityPublishOptions;
  };
}

export class SanityService {
  /**
   * Test connection to Sanity CMS
   */
  static async testConnection(request: SanityConnectionTest): Promise<{ message: string }> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/test-connection`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to connect to Sanity");
    }

    return response.json();
  }

  /**
   * Fetch available document types from Sanity schema with validation
   */
  static async getDocumentTypes(request: SanityConnectionTest): Promise<SanityDocumentTypesResponse> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/document-types`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch document types");
    }

    return response.json();
  }

  /**
   * Validate a specific schema for compatibility
   */
  static async validateSchema(request: SanityConnectionTest & { documentType: string }): Promise<SanitySchemaValidationResponse> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/validate-schema`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to validate schema");
    }

    return response.json();
  }

  /**
   * Publish a single article to Sanity
   */
  static async publishArticle(request: SanityPublishRequest): Promise<SanityPublishResponse> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to publish to Sanity");
    }

    return response.json();
  }

  /**
   * Publish multiple articles to Sanity in a single transaction
   */
  static async batchPublishArticles(requests: SanityPublishRequest[]): Promise<SanityBatchPublishResponse> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/batch-publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(requests),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to batch publish to Sanity");
    }

    return response.json();
  }

  /**
   * Execute a GROQ query against Sanity
   */
  static async query(request: SanityQueryRequest): Promise<SanityQueryResponse> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to execute query");
    }

    return response.json();
  }

  /**
   * Update Sanity integration settings
   */
  static async updateSettings(request: SanityUpdateSettingsRequest): Promise<SanitySettingsResponse> {
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/update-settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update Sanity settings");
    }

    return response.json();
  }

  /**
   * Validate Sanity configuration
   */
  static validateCredentials(credentials: SanityCredentials): string[] {
    const errors: string[] = [];

    if (!credentials.projectId?.trim()) {
      errors.push("Project ID is required");
    }

    if (!credentials.dataset?.trim()) {
      errors.push("Dataset is required");
    }

    if (!credentials.token?.trim()) {
      errors.push("API token is required");
    }

    // Validate project ID format (basic check)
    if (credentials.projectId && !/^[a-z0-9]+$/.test(credentials.projectId)) {
      errors.push("Project ID should contain only lowercase letters and numbers");
    }

    // Validate dataset format (basic check)
    if (credentials.dataset && !/^[a-zA-Z0-9_-]+$/.test(credentials.dataset)) {
      errors.push("Dataset name contains invalid characters");
    }

    // Validate token format (basic check)
    if (credentials.token && !credentials.token.startsWith("sk")) {
      errors.push("API token should start with 'sk'");
    }

    // Validate studio URL if provided
    if (credentials.studioUrl) {
      try {
        new URL(credentials.studioUrl);
      } catch {
        errors.push("Studio URL is not a valid URL");
      }
    }

    return errors;
  }

  /**
   * Check if a document type is compatible for publishing
   */
  static isDocumentTypeCompatible(validation: SanityValidationResult): boolean {
    return validation.valid && (!validation.errors || validation.errors.length === 0);
  }

  /**
   * Get compatibility score for a document type (0-100)
   */
  static getCompatibilityScore(validation: SanityValidationResult): number {
    if (!validation.valid) return 0;
    
    let score = 100;
    
    // Deduct points for warnings
    if (validation.warnings) {
      score -= validation.warnings.length * 10;
    }
    
    // Deduct points for skipped fields
    if (validation.skipped) {
      score -= validation.skipped.length * 5;
    }
    
    return Math.max(0, score);
  }

  /**
   * Generate default publish options
   */
  static getDefaultPublishOptions(): SanityPublishOptions {
    return {
      returnIds: true,
      returnDocuments: false,
      visibility: "sync",
      dryRun: false,
      documentType: "post",
      imageFieldName: "mainImage",
    };
  }

  /**
   * Build studio URL for a document
   */
  static buildStudioUrl(credentials: SanityCredentials, documentType: string, documentId: string): string {
    const baseUrl = credentials.studioUrl || `https://${credentials.projectId}.sanity.studio`;
    return `${baseUrl}/structure/${documentType};${documentId}`;
  }

  /**
   * Get recommended document types based on compatibility scores
   */
  static getRecommendedDocumentTypes(validation: Record<string, SanityValidationResult>): string[] {
    return Object.entries(validation)
      .filter(([_, val]) => this.isDocumentTypeCompatible(val))
      .sort(([_, a], [__, b]) => this.getCompatibilityScore(b) - this.getCompatibilityScore(a))
      .map(([type, _]) => type);
  }
}

export default SanityService;